import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Share2, X, Copy, Check, Mail } from "lucide-react";
import { WhatsAppGlyph } from "@/components/WhatsAppButton";
import { toast } from "sonner";

const SHARE_URL = "https://superagehealth.com";
const SHARE_TITLE = "SuperAge Health™ — Precision Liver Evaluation";
// Long copy — used ONLY for platforms that don't render OG link previews
// (email, X/Twitter). WhatsApp / LinkedIn / native share auto-fetch the
// rich preview from the URL's meta tags, so sending it as text creates
// visible duplication.
const SHARE_LONG =
  "Take a 2-min free liver assessment to know your risk, then get a personalised 12-week Liver Recovery Plan.";
// Short tweet-safe copy
const SHARE_TWEET =
  "Know your liver's risk in 2 minutes. India's first Precision Liver Evaluation platform.";

// LinkedIn / X (Twitter) brand icons — lucide has no brand marks
const LinkedInGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.049c.476-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.063 2.063 0 112.063 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z" />
  </svg>
);
const XGlyph = ({ className }) => (
  <svg viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden>
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

export const ShareButton = () => {
  const [open, setOpen] = useState(false);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    // Web Share API is best on mobile; desktop browsers often expose it too
    setCanNativeShare(
      typeof navigator !== "undefined" && typeof navigator.share === "function"
    );
  }, []);

  const handleClick = async () => {
    if (canNativeShare) {
      try {
        // Send URL only — the receiving app renders the OG rich preview.
        // Passing `text` would show below the preview and duplicate content.
        await navigator.share({ title: SHARE_TITLE, url: SHARE_URL });
        return;
      } catch (e) {
        if (e && e.name === "AbortError") return;
      }
    }
    setOpen(true);
  };

  const shareLinks = {
    // WhatsApp + LinkedIn render OG previews → URL only, no duplicate copy
    whatsapp: `https://wa.me/?text=${encodeURIComponent(SHARE_URL)}`,
    linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(SHARE_URL)}`,
    // X shows a compact card but the tweet body is user-facing, so add copy
    twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(SHARE_TWEET)}&url=${encodeURIComponent(SHARE_URL)}`,
    // Email has no preview → include full copy + link
    email: `mailto:?subject=${encodeURIComponent(SHARE_TITLE)}&body=${encodeURIComponent(SHARE_LONG + "\n\n" + SHARE_URL)}`,
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(SHARE_URL);
      setCopied(true);
      toast.success("Link copied");
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error("Could not copy — long-press the link instead");
    }
  };

  return (
    <>
      <motion.button
        type="button"
        onClick={handleClick}
        data-testid="share-float"
        aria-label="Share SuperAge Health"
        initial={{ opacity: 0, scale: 0.6 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 1.2, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
        whileHover={{ scale: 1.08 }}
        whileTap={{ scale: 0.94 }}
        className="fixed bottom-24 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-sa-forest text-white shadow-[0_10px_30px_-6px_rgba(10,46,34,0.55)] ring-1 ring-white/10"
      >
        <Share2 className="h-6 w-6" strokeWidth={1.9} />
      </motion.button>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-[60] flex items-end justify-center bg-black/40 backdrop-blur-sm sm:items-center"
            onClick={() => setOpen(false)}
            data-testid="share-sheet"
          >
            <motion.div
              onClick={(e) => e.stopPropagation()}
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 40, opacity: 0 }}
              transition={{ duration: 0.28, ease: [0.22, 1, 0.36, 1] }}
              className="w-full max-w-md rounded-t-[28px] bg-sa-bg p-6 sm:rounded-[28px]"
            >
              <div className="mb-5 flex items-start justify-between">
                <div>
                  <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-sa-copper">Share</p>
                  <h3 className="mt-1 font-display text-2xl font-medium text-sa-forest">
                    Spread the word
                  </h3>
                  <p className="mt-1 text-sm text-sa-muted">
                    Help someone know their liver's risk in 2 minutes.
                  </p>
                </div>
                <button
                  onClick={() => setOpen(false)}
                  aria-label="Close"
                  data-testid="share-close"
                  className="rounded-full p-1.5 text-sa-muted hover:bg-sa-beige hover:text-sa-forest"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="grid grid-cols-4 gap-3">
                <ShareTile
                  href={shareLinks.whatsapp}
                  label="WhatsApp"
                  color="#25D366"
                  icon={<WhatsAppGlyph className="h-5 w-5 text-white" />}
                  testid="share-whatsapp"
                />
                <ShareTile
                  href={shareLinks.linkedin}
                  label="LinkedIn"
                  color="#0A66C2"
                  icon={<LinkedInGlyph className="h-5 w-5 text-white" />}
                  testid="share-linkedin"
                />
                <ShareTile
                  href={shareLinks.twitter}
                  label="X"
                  color="#0F1419"
                  icon={<XGlyph className="h-5 w-5 text-white" />}
                  testid="share-x"
                />
                <ShareTile
                  href={shareLinks.email}
                  label="Email"
                  color="#5B6660"
                  icon={<Mail className="h-5 w-5 text-white" strokeWidth={1.9} />}
                  testid="share-email"
                  external={false}
                />
              </div>

              <button
                onClick={copyLink}
                data-testid="share-copy"
                className="mt-5 flex w-full items-center justify-between rounded-full border border-sa-beige bg-white px-5 py-3 text-left text-sm text-sa-forest transition-colors hover:border-sa-forest/40"
              >
                <span className="truncate pr-3 text-sa-muted">{SHARE_URL}</span>
                <span className="inline-flex items-center gap-1.5 font-medium">
                  {copied ? (
                    <>
                      <Check className="h-4 w-4 text-emerald-600" /> Copied
                    </>
                  ) : (
                    <>
                      <Copy className="h-4 w-4" /> Copy
                    </>
                  )}
                </span>
              </button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

const ShareTile = ({ href, label, color, icon, testid, external = true }) => (
  <a
    href={href}
    target={external ? "_blank" : undefined}
    rel={external ? "noopener noreferrer" : undefined}
    data-testid={testid}
    className="group flex flex-col items-center gap-1.5"
  >
    <span
      className="flex h-12 w-12 items-center justify-center rounded-2xl shadow-sm transition-transform group-hover:scale-105"
      style={{ background: color }}
    >
      {icon}
    </span>
    <span className="text-[11px] font-medium text-sa-forest">{label}</span>
  </a>
);

export default ShareButton;
